import { Link } from "@tanstack/react-router";
import { Activity, Loader2 } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { downBanner, landingCopy, startingProgress, type BrainState } from "@/lib/brain-state";

type DownState = Exclude<BrainState, "ok">;

// The one place the page says the brain is down: under the header, never per tab.
export function BrainDownBanner({
  state,
  name,
  since,
  retrying,
  onRetry,
  selfHosted = false,
}: {
  state: DownState;
  name: string;
  // Runs on the org's own machine: nothing on Boo Lab's side is bringing it back.
  selfHosted?: boolean;
  // When the Boo started coming up, for the starting bar.
  since: number;
  retrying: boolean;
  onRetry: () => void;
}) {
  const copy = downBanner(state, name, { selfHosted });
  if (state === "starting" && !selfHosted) {
    const pct = startingProgress(since);
    return (
      <div
        role="status"
        className="mx-6 mt-2 flex flex-col gap-2.5 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3.5 dark:border-blue-500/30 dark:bg-blue-950/30"
      >
        <div className="flex flex-col gap-0.5">
          <strong className="text-sm font-semibold text-blue-950 dark:text-blue-100">{copy.title}</strong>
          <span className="text-[13px] text-blue-800 dark:text-blue-200">{copy.body}</span>
        </div>
        <div
          role="progressbar"
          aria-label="Starting up"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={pct}
          className="h-1.5 overflow-hidden rounded-full bg-blue-100 dark:bg-blue-900/50"
        >
          <div className="h-full rounded-full bg-blue-500 transition-[width] duration-1000" style={{ width: `${pct}%` }} />
        </div>
      </div>
    );
  }
  return (
    <div
      role="status"
      className="mx-6 mt-2 flex flex-wrap items-center gap-4 rounded-lg border border-orange-200 bg-orange-50 px-4 py-3.5 dark:border-orange-500/30 dark:bg-orange-950/30"
    >
      <div className="flex min-w-0 flex-[1_1_360px] flex-col gap-0.5">
        <strong className="text-sm font-semibold text-orange-950 dark:text-orange-100">{copy.title}</strong>
        <span className="text-[13px] text-orange-800 dark:text-orange-200">{copy.body}</span>
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={retrying}
        onClick={onRetry}
        className="border-orange-300 bg-white font-semibold text-orange-800 hover:bg-orange-50 hover:text-orange-900 dark:border-orange-500/40 dark:bg-transparent dark:text-orange-200"
      >
        {retrying && <Loader2 className="size-3.5 animate-spin" />}
        {selfHosted ? "Check again" : "Try again now"}
      </Button>
    </div>
  );
}

// Subtitles the brain tabs show above their content, kept so the landing
// panel reads like the tab it stands in for.
const SUBTITLE: Record<string, string> = {
  channels: "Who Boo answers, where it listens, and what it says when it can't take part.",
};

// What a brain tab shows while the brain is down, when someone lands on it by
// link. Calm, never a red error.
export function BrainTabLanding({
  tab,
  label,
  state,
  name,
  orgId,
  containerId,
}: {
  tab: string;
  label: string;
  state: DownState;
  name: string;
  orgId: string;
  containerId: string;
}) {
  const copy = landingCopy(tab, state, name);
  return (
    <div className="flex flex-1 flex-col min-h-0 overflow-auto p-6 gap-5">
      <PageHeader title={label} subtitle={SUBTITLE[tab]} />
      {state === "starting" ? (
        <div className="flex max-w-4xl flex-col gap-2.5">
          <div className="h-13 rounded-lg bg-muted" />
          <div className="h-13 rounded-lg bg-muted" />
          <div className="h-13 rounded-lg bg-muted opacity-60" />
          <p className="mt-2 text-sm text-muted-foreground">{copy.body}</p>
        </div>
      ) : (
        <div className="flex max-w-4xl flex-wrap items-start gap-4 rounded-xl border border-dashed bg-muted/40 p-7">
          <Activity aria-hidden="true" className="size-7 shrink-0 text-muted-foreground/70" strokeWidth={1.6} />
          <div className="flex min-w-0 flex-[1_1_320px] flex-col gap-1.5">
            <strong className="text-[15px] font-semibold">{copy.title}</strong>
            <p className="max-w-[60ch] text-sm text-muted-foreground">{copy.body}</p>
            <p className="mt-1.5 text-[13px] text-muted-foreground">
              Meanwhile you can check its{" "}
              <Link to="/orgs/$orgId/containers/$containerId" params={{ orgId, containerId }} className="text-foreground underline underline-offset-2">
                connections
              </Link>{" "}
              or{" "}
              <Link to="/orgs/$orgId/containers/$containerId/usage" params={{ orgId, containerId }} className="text-foreground underline underline-offset-2">
                usage
              </Link>
              .
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
